import AppForm from "@/components/appForm";
import { useAuthenticator } from "@aws-amplify/ui-react";
import { apiGetRequest, apiPutRequest, apiPostRequest, logCourseAction } from "@/functions/requests";
import { useApp, useProblem } from "@/hooks/contextHooks";
import { Box, Button, Card, CardBody, CardFooter, CardHeader, Center, Flex, Heading, Text, useToast, Badge, SimpleGrid, useColorModeValue, Modal, ModalOverlay, ModalContent, ModalHeader, ModalCloseButton, ModalBody, ModalFooter, Table, Thead, Tbody, Tr, Th, Td, Spinner, Avatar, AvatarGroup, Tooltip, VStack, HStack, Divider, IconButton } from "@chakra-ui/react";
import { faEdit, faRocket, faSave, faVideo, faListAlt, faShare, faCopy, faArrowLeft, faEye, faChartLine, faUsers } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { AppMeta, CourseLog } from "@xnav/types/src/common";
import axios from "axios";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Loading from "@/components/loading";

const getActionBadge = (action: string) => {
  const act = (action || '').toLowerCase();
  if (act.includes('deploy') && !act.includes('disapprov')) {
    return { bg: "rgba(16, 185, 129, 0.14)", color: "#10b981" };
  }
  if (act.includes('disapprov') || act.includes('delete') || act.includes('cancel')) {
    return { bg: "rgba(239, 68, 68, 0.14)", color: "#f87171" };
  }
  if (act.includes('structure') || act.includes('section') || act.includes('quiz')) {
    return { bg: "rgba(14, 165, 233, 0.14)", color: "#38bdf8" };
  }
  return { bg: "rgba(99, 102, 241, 0.14)", color: "#818cf8" };
};

const DetailsCell: React.FC<{ details: string }> = ({ details }) => {
  const textColor = useColorModeValue('gray.700', 'gray.200');

  if (!details) return <Text color="gray.500" fontSize="xs" fontStyle="italic">-</Text>;

  return (
    <Box minW="260px" maxW="580px" py={0.5}>
      <Text 
        fontSize="xs" 
        color={textColor}
        lineHeight="1.5"
        wordBreak="break-word"
        whiteSpace="pre-wrap"
        fontWeight="medium"
      >
        {details}
      </Text>
    </Box>
  );
};

export default function AppMenu() {
	const { user } = useAuthenticator()
	const [userRole, setUserRole] = useState<string | null>(null);
	const navigate = useNavigate()
	const [app, setApp] = useState(useApp() as AppMeta)
	const [editingApp, setEditingApp] = useState(false)
	const [viewingLogs, setViewingLogs] = useState(false)
	const [appLogs, setAppLogs] = useState<CourseLog[]>([])
	const [logsLoading, setLogsLoading] = useState(false)
	const toast = useToast()

	const [shareModalOpen, setShareModalOpen] = useState(false)
	const [logToShare, setLogToShare] = useState<CourseLog | null>(null)
	const [shareMembers, setShareMembers] = useState<any[]>([])
	const [shareUserOrg, setShareUserOrg] = useState('')
	const [loadingShareMembers, setLoadingShareMembers] = useState(false)

	useEffect(() => {
		if (shareModalOpen && user?.signInDetails?.loginId) {
			const fetchShareData = async () => {
				setLoadingShareMembers(true);
				try {
					const callerEmail = user?.signInDetails?.loginId || '';
					if (!callerEmail) return;
					const callerData = await apiGetRequest<undefined, { role: string; org?: string }>(`/users/${encodeURIComponent(callerEmail)}`);
					const org = callerData?.org || 'Global';
					setShareUserOrg(org);

					const allUsers = await apiGetRequest<undefined, any[]>('/users');
					const orgMembers = (allUsers || []).filter(u => u.org.toLowerCase().trim() === org.toLowerCase().trim());
					
					const resolvedMembers = await Promise.all(orgMembers.map(async (member) => {
						let avatarUrl = `https://api.dicebear.com/7.x/adventurer/svg?seed=${encodeURIComponent(member.email)}`;
						if (member.picture) {
							if (member.picture.startsWith('http')) {
								avatarUrl = member.picture;
							} else {
								try {
									const res = await apiGetRequest<undefined, { signedUrl: string }>(`/get-profile-image?mediaKeyId=${encodeURIComponent(member.picture)}`);
									if (res?.signedUrl) {
										avatarUrl = res.signedUrl;
									}
								} catch (e) {
									console.error("Error signing avatar url for user:", member.email, e);
								}
							}
						}
						return {
							...member,
							avatarUrl
						};
					}));
					setShareMembers(resolvedMembers);
				} catch (e) {
					console.error("Failed to load contacts for log sharing", e);
				} finally {
					setLoadingShareMembers(false);
				}
			};
			fetchShareData();
		}
	}, [shareModalOpen, user]);

	const handleShareLog = (log: CourseLog) => {
		setLogToShare(log);
		setShareModalOpen(true);
	};

	const executeShare = async (targetEmail?: string) => {
		if (!logToShare || !user?.signInDetails?.loginId) return;
		const callerEmail = user.signInDetails.loginId;
		
		let chatId = '';
		let targetName = '';
		if (targetEmail) {
			const sorted = [callerEmail.toLowerCase().trim(), targetEmail.toLowerCase().trim()].sort();
			chatId = `${shareUserOrg}#direct#${sorted[0]}#${sorted[1]}`;
			targetName = targetEmail.split('@')[0];
		} else {
			chatId = `${shareUserOrg}#broadcast`;
			targetName = "Broadcast Channel";
		}

		try {
			await apiPostRequest('/chats', {
				chatId,
				message: `[Shared Course Audit Log: ${logToShare.action}]`,
				sharedLog: JSON.stringify(logToShare)
			});
			toast({
				title: "Log Shared",
				description: `Audit log shared successfully to ${targetName}.`,
				status: "success",
				duration: 3000,
				isClosable: true
			});
			setShareModalOpen(false);
			setLogToShare(null);
		} catch (e) {
			console.error("Failed to share log", e);
			toast({
				title: "Error",
				description: "Failed to share audit log.",
				status: "error",
				duration: 3000
			});
		}
	};

	useEffect(() => {
		const fetchRole = async () => {
			if (user?.signInDetails?.loginId) {
				try {
					const data = await apiGetRequest<undefined, { role: string }>(`/users/${user.signInDetails.loginId}`);
					setUserRole(data?.role || 'Super Admin');
				} catch (error) {
					console.error("Failed to fetch user role", error);
					setUserRole('Super Admin');
				}
			}
		};
		fetchRole();
	}, [user]);

	const isCreatorOrAdmin = userRole === 'Super Admin' || userRole === 'Admin' || app.creatorId === user?.userId;

	const isCreatorOrCollab = isCreatorOrAdmin || 
		(user?.userId && app.collaborators?.includes(user?.userId)) || 
		(user?.signInDetails?.loginId && app.collaborators?.some(c => c.toLowerCase() === user.signInDetails.loginId.toLowerCase()));
	
	const isOnlyReviewer = !isCreatorOrCollab && (
		(user?.userId && app.reviewers?.includes(user?.userId)) || 
		(user?.signInDetails?.loginId && app.reviewers?.some(r => r.toLowerCase() === user.signInDetails.loginId.toLowerCase()))
	);

	const canDeleteOrDeploy = userRole === 'Super Admin' || userRole === 'Admin' || userRole === 'Org Admin';

	async function patchApp(newApp: AppMeta, file: File | undefined) {
		const toastRef = toast({
			title: `Updating ${newApp.name}`,
			description: 'Please wait...',
			status: 'warning',
			position: 'bottom-left',
			isClosable: true
		})
		await apiPutRequest(`apps/${newApp.id}`, newApp)
		
		let detailsMsg: string[] = [];
		if (newApp.name !== app.name) detailsMsg.push("Name changed");
		if (newApp.description !== app.description) detailsMsg.push("Description changed");
		if (JSON.stringify(newApp.tags) !== JSON.stringify(app.tags)) detailsMsg.push("Tags changed");
		if (JSON.stringify(newApp.collaborators) !== JSON.stringify(app.collaborators)) detailsMsg.push("Collaborators changed");
		if (JSON.stringify(newApp.reviewers) !== JSON.stringify(app.reviewers)) detailsMsg.push("Reviewers changed");

		if (file) {
			const putAppIconUrl = await apiPutRequest<undefined, string>(`appsIcon/${newApp.id}`, undefined)
			await axios.put(putAppIconUrl, file)
			detailsMsg.push("Icon changed");
		}
		
		const detailsStr = detailsMsg.length > 0 ? detailsMsg.join(", ") : "Updated metadata";
		await logCourseAction(newApp.id, "Updated Course Info", detailsStr);

		setApp(newApp)
		toast.close(toastRef)
	}

	async function fetchAppLogs() {
		setViewingLogs(true);
		setLogsLoading(true);
		try {
			const data = await apiGetRequest<undefined, CourseLog[]>(`/logs/${app.id}`);
			setAppLogs(data.sort((a, b) => b.timestamp - a.timestamp));
		} catch (error) {
			console.error("Failed to fetch app logs", error);
			toast({ title: 'Error', description: 'Failed to fetch audit logs.', status: 'error', duration: 3000 });
		} finally {
			setLogsLoading(false);
		}
	}

	if (editingApp) return (
		<Center flex={1} w='100%' flexDir='column' maxH='100%' p={4}>
			<Card maxH='90%' minW={{ base: "100%", md: "650px" }} borderRadius="2xl" border="1px solid" borderColor={useColorModeValue("gray.200", "#374151")} bg={useColorModeValue("white", "#111827")}>
				<CardHeader pb={2}>
					<Center>
						<Heading size="md" fontWeight="800"> Edit {app.name} </Heading>
					</Center>
				</CardHeader>
				<CardBody display='flex' flexDir='column' overflowY='auto'>
					<AppForm
						app={app}
						onAdd={(newApp, icon) => {
							patchApp(newApp, icon)
							setEditingApp(false)
						}}
						onCancel={() => setEditingApp(false)}
						onValidate={() => undefined}
					/>
				</CardBody>
			</Card>
		</Center>
	)

	const cardBg = useColorModeValue("white", "#111827");
	const cardSubtleBg = useColorModeValue("gray.50", "#1f2937");
	const cardBorder = useColorModeValue("gray.200", "#374151");
	const titleColor = useColorModeValue("gray.900", "white");
	const mutedTextColor = useColorModeValue("gray.500", "gray.400");

	return (
		<Center flex={1} w='100%' flexDir='column' p={{ base: 4, md: 6 }}>
			<Card 
				w='100%' 
				maxW='880px' 
				p={{ base: 5, md: 8 }} 
				borderRadius="2xl" 
				bg={cardBg} 
				border="1px solid" 
				borderColor={cardBorder} 
				shadow="sm"
			>
				<CardHeader mb={2} position="relative" pb={2} pt={0}>
					<Center flexDir="column" gap={3}>
						<Heading size="xl" fontWeight="800" letterSpacing="-0.5px" color={titleColor} textAlign="center">
							{app.name}
						</Heading>
						<HStack 
							bg={cardSubtleBg} 
							px={3} 
							py={1} 
							borderRadius="lg" 
							border="1px solid"
							borderColor={cardBorder}
							spacing={2}
						>
							<Text fontFamily="mono" fontSize="11px" color={mutedTextColor}>
								ID: {app.id}
							</Text>
							<Tooltip label="Copy Course ID" placement="top" hasArrow borderRadius="md">
								<IconButton
									aria-label="Copy ID"
									icon={<FontAwesomeIcon icon={faCopy} size="xs" />}
									size="xs"
									variant="ghost"
									color={mutedTextColor}
									_hover={{ color: "#0ea5e9" }}
									onClick={() => {
										navigator.clipboard.writeText(app.id);
										toast({
											title: "Course ID Copied",
											description: app.id,
											status: "success",
											duration: 2000,
											isClosable: true
										});
									}}
								/>
							</Tooltip>
						</HStack>
					</Center>
				</CardHeader>
				<CardBody py={4}>
					<Flex flexDir='column' gap={6}>
						{/* Stats Grid */}
						<SimpleGrid columns={{ base: 1, sm: 2, md: 3 }} spacing={4} textAlign="center">
							<Box 
								p={4} 
								borderRadius="xl" 
								bg={cardSubtleBg} 
								border="1px solid" 
								borderColor={cardBorder}
							>
								<Text fontSize="10px" fontWeight="700" color={mutedTextColor} textTransform="uppercase" letterSpacing="wider" mb={1}>Creator</Text>
								<Text fontSize="sm" fontWeight="700" color={titleColor} noOfLines={1}>{app.creator || 'Unknown'}</Text>
							</Box>

							<Box 
								p={4} 
								borderRadius="xl" 
								bg={cardSubtleBg} 
								border="1px solid" 
								borderColor={cardBorder}
							>
								<Text fontSize="10px" fontWeight="700" color={mutedTextColor} textTransform="uppercase" letterSpacing="wider" mb={1}>Organization</Text>
								<Text fontSize="sm" fontWeight="700" color="#0284c7">{app.org || app.orgId || 'System Default'}</Text>
							</Box>

							<Box 
								p={4} 
								borderRadius="xl" 
								bg={cardSubtleBg} 
								border="1px solid" 
								borderColor={cardBorder}
								display="flex"
								flexDir="column"
								alignItems="center"
								justifyContent="center"
							>
								<Text fontSize="10px" fontWeight="700" color={mutedTextColor} textTransform="uppercase" letterSpacing="wider" mb={1}>Status</Text>
								{app.deployed == undefined ? (
									<Badge bg="rgba(156, 163, 175, 0.15)" color="gray.400" px={2.5} py={0.5} borderRadius="md" fontSize="11px" fontWeight="700">Draft</Badge>
								) : app.deployed === app.edited ? (
									<Badge bg="rgba(14, 165, 233, 0.1)" color="#0284c7" px={2.5} py={0.5} borderRadius="md" fontSize="11px" fontWeight="700">Live</Badge>
								) : (
									<Badge bg="rgba(234, 179, 8, 0.1)" color="#eab308" px={2.5} py={0.5} borderRadius="md" fontSize="11px" fontWeight="700">Live (Edited)</Badge>
								)}
							</Box>

							<Box 
								p={4} 
								borderRadius="xl" 
								bg={cardSubtleBg} 
								border="1px solid" 
								borderColor={cardBorder}
							>
								<Text fontSize="10px" fontWeight="700" color={mutedTextColor} textTransform="uppercase" letterSpacing="wider" mb={1}>Created</Text>
								<Text fontSize="sm" fontWeight="700" color={titleColor}>{new Date(app.created).toLocaleDateString()}</Text>
							</Box>

							<Box 
								p={4} 
								borderRadius="xl" 
								bg={cardSubtleBg} 
								border="1px solid" 
								borderColor={cardBorder}
							>
								<Text fontSize="10px" fontWeight="700" color={mutedTextColor} textTransform="uppercase" letterSpacing="wider" mb={1}>Last Edited</Text>
								<Text fontSize="sm" fontWeight="700" color={titleColor}>{app.edited ? new Date(app.edited).toLocaleDateString() : '-'}</Text>
							</Box>

							<Box 
								p={4} 
								borderRadius="xl" 
								bg={cardSubtleBg} 
								border="1px solid" 
								borderColor={cardBorder}
							>
								<Text fontSize="10px" fontWeight="700" color={mutedTextColor} textTransform="uppercase" letterSpacing="wider" mb={1}>Deployed</Text>
								<Text fontSize="sm" fontWeight="700" color={titleColor}>{app.deployed ? new Date(app.deployed).toLocaleDateString() : '-'}</Text>
							</Box>
						</SimpleGrid>

						{app.tags && app.tags.length > 0 && (
							<Flex gap={2} flexWrap="wrap" justifyContent="center">
								{app.tags.map((t: string) => (
									<Badge key={t} bg="rgba(14, 165, 233, 0.08)" color="#0284c7" px={3} py={1} borderRadius="md" textTransform="lowercase" fontSize="xs" fontWeight="600">
										#{t}
									</Badge>
								))}
							</Flex>
						)}

						{/* Edit Info, View Logs & Student Analytics Buttons */}
						{!isOnlyReviewer && (
							<Flex justifyContent='center' gap={3} flexWrap="wrap">
								{isCreatorOrAdmin && (
									<Button
										leftIcon={<FontAwesomeIcon icon={faEdit} />}
										onClick={() => setEditingApp(true)}
										variant="ghost"
										color={mutedTextColor}
										_hover={{ color: useColorModeValue("gray.900", "white"), bg: cardSubtleBg }}
										borderRadius="lg"
										size="xs"
										fontSize="xs"
									>
										Edit App Info
									</Button>
								)}
								<Button
									leftIcon={<FontAwesomeIcon icon={faListAlt} />}
									onClick={fetchAppLogs}
									variant="ghost"
									color="#0ea5e9"
									_hover={{ bg: "rgba(14, 165, 233, 0.1)" }}
									borderRadius="lg"
									size="xs"
									fontSize="xs"
								>
									View Audit Logs
								</Button>
								<Button
									leftIcon={<FontAwesomeIcon icon={faChartLine} />}
									onClick={() => navigate(`/${app.id}/analytics`)}
									variant="ghost"
									color="#06b6d4"
									_hover={{ bg: "rgba(6, 182, 212, 0.1)" }}
									borderRadius="lg"
									size="xs"
									fontSize="xs"
									fontWeight="700"
								>
									Student Analytics
								</Button>
							</Flex>
						)}
					</Flex>
				</CardBody>
				<CardFooter pt={4} pb={2}>
					<Center w='100%'>
						<SimpleGrid columns={{ base: 1, md: isOnlyReviewer ? 1 : 3 }} spacing={4} w={isOnlyReviewer ? "auto" : "100%"}>
							{!isOnlyReviewer && (
								<Button
									h="48px"
									fontSize="sm"
									fontWeight="700"
									onClick={() => navigate(`/${app.id}/media`)}
									variant='outline'
									borderColor={cardBorder}
									leftIcon={<FontAwesomeIcon icon={faVideo} />}
									borderRadius="xl"
									_hover={{ bg: cardSubtleBg }}
									transition="all 0.15s ease"
								>
									Manage Media
								</Button>
							)}
							<Button
								h="48px"
								fontSize="sm"
								fontWeight="700"
								onClick={() => navigate(`/${app.id}/editor`)}
								bgGradient="linear(to-r, #0ea5e9, #10b981)"
								color="white"
								_hover={{ bgGradient: "linear(to-r, #0284c7, #059669)" }}
								leftIcon={<FontAwesomeIcon icon={faEdit} />}
								borderRadius="xl"
								transition="all 0.15s ease"
							>
								{isOnlyReviewer ? 'View Contents' : 'Edit Contents'}
							</Button>
							{!isOnlyReviewer && canDeleteOrDeploy && (
								<Button
									h="48px"
									fontSize="sm"
									fontWeight="700"
									onClick={() => navigate(`/${app.id}/deploy`)}
									variant='outline'
									borderColor={cardBorder}
									leftIcon={<FontAwesomeIcon icon={faRocket} />}
									borderRadius="xl"
									_hover={{ bg: cardSubtleBg }}
									transition="all 0.15s ease"
								>
									Deploy App
								</Button>
							)}
						</SimpleGrid>
					</Center>
				</CardFooter>
			</Card>

			{/* Audit Logs Modal */}
			<Modal isOpen={viewingLogs} onClose={() => setViewingLogs(false)} size="4xl" scrollBehavior="inside">
				<ModalOverlay backdropFilter="blur(8px)" bg="rgba(0, 0, 0, 0.7)" />
				<ModalContent borderRadius="2xl" bg={cardBg} border="1px solid" borderColor={cardBorder} shadow="2xl">
					<ModalHeader borderBottom="1px solid" borderColor={cardBorder} py={3.5}>
						<Text fontSize="sm" fontWeight="700" color={titleColor}>Audit Logs - {app.name}</Text>
					</ModalHeader>
					<ModalCloseButton />
					<ModalBody p={5}>
						{logsLoading ? (
							<Center p={10}><Spinner size="lg" color="#0ea5e9" thickness="3px" /></Center>
						) : appLogs.length === 0 ? (
							<Center p={10} color={mutedTextColor} fontSize="sm">No audit logs found for this course.</Center>
						) : (
							<Box overflowX="auto" borderRadius="xl" border="1px solid" borderColor={cardBorder}>
								<Table variant="simple" size="sm">
									<Thead bg={cardSubtleBg}>
										<Tr>
											<Th py={2.5} px={3} color={mutedTextColor} fontSize="10px" fontWeight="700" letterSpacing="wider">TIMESTAMP</Th>
											<Th py={2.5} px={3} color={mutedTextColor} fontSize="10px" fontWeight="700" letterSpacing="wider">USER</Th>
											<Th py={2.5} px={3} color={mutedTextColor} fontSize="10px" fontWeight="700" letterSpacing="wider">ACTION</Th>
											<Th py={2.5} px={3} color={mutedTextColor} fontSize="10px" fontWeight="700" letterSpacing="wider">DETAILS</Th>
											<Th py={2.5} px={3} color={mutedTextColor} fontSize="10px" fontWeight="700" letterSpacing="wider" isNumeric>ACTIONS</Th>
										</Tr>
									</Thead>
									<Tbody>
										{appLogs.map(log => {
											const badgeStyle = getActionBadge(log.action);
											return (
												<Tr key={log.id} _hover={{ bg: cardSubtleBg }}>
													<Td py={3} px={3.5} whiteSpace="nowrap" color={mutedTextColor} fontSize="xs" verticalAlign="top">
														{new Date(log.timestamp).toLocaleString()}
													</Td>
													<Td py={3} px={3.5} fontWeight="600" fontSize="xs" color={titleColor} verticalAlign="top" maxW="200px" isTruncated title={log.userEmail}>
														{log.userEmail}
													</Td>
													<Td py={3} px={3.5} verticalAlign="top">
														<Badge bg={badgeStyle.bg} color={badgeStyle.color} px={2} py={0.5} borderRadius="md" fontSize="9px" fontWeight="700" textTransform="uppercase">
															{log.action}
														</Badge>
													</Td>
													<Td py={3} px={3.5} verticalAlign="top">
														<DetailsCell details={log.details} />
													</Td>
													<Td py={3} px={3.5} isNumeric verticalAlign="top">
														<Button
															size="xs"
															variant="ghost"
															color="#0ea5e9"
															_hover={{ bg: "rgba(14, 165, 233, 0.1)" }}
															leftIcon={<FontAwesomeIcon icon={faShare} />}
															onClick={() => handleShareLog(log)}
															borderRadius="md"
														>
															Share
														</Button>
													</Td>
												</Tr>
											);
										})}
									</Tbody>
								</Table>
							</Box>
						)}
					</ModalBody>
					<ModalFooter borderTop="1px solid" borderColor={cardBorder} py={3}>
						<Button size="sm" variant="ghost" onClick={() => setViewingLogs(false)} borderRadius="lg">Close</Button>
					</ModalFooter>
				</ModalContent>
			</Modal>

			{/* Share Log Modal */}
			<Modal isOpen={shareModalOpen} onClose={() => setShareModalOpen(false)} isCentered>
				<ModalOverlay backdropFilter="blur(8px)" bg="rgba(0, 0, 0, 0.7)" />
				<ModalContent borderRadius="2xl" bg={cardBg} border="1px solid" borderColor={cardBorder} shadow="2xl">
					<ModalHeader borderBottom="1px solid" borderColor={cardBorder} py={3.5}>
						<Text fontSize="sm" fontWeight="700">Share Audit Log</Text>
					</ModalHeader>
					<ModalCloseButton />
					<ModalBody p={5}>
						<VStack align="stretch" spacing={4}>
							<Text fontSize="xs" color={mutedTextColor}>
								Choose where you want to share this course audit log entry:
							</Text>

							{loadingShareMembers ? (
								<Center py={6}><Spinner color="#0ea5e9" /></Center>
							) : (
								<VStack align="stretch" spacing={2} maxH="300px" overflowY="auto">
									<Flex
										p={2.5}
										align="center"
										justify="space-between"
										borderRadius="xl"
										border="1px solid"
										borderColor="rgba(14, 165, 233, 0.2)"
										bg="rgba(14, 165, 233, 0.05)"
										cursor="pointer"
										onClick={() => executeShare()}
										_hover={{ bg: "rgba(14, 165, 233, 0.1)" }}
										transition="all 0.15s ease"
									>
										<HStack spacing={2.5}>
											<Flex w={7} h={7} borderRadius="lg" bgGradient="linear(to-br, #0ea5e9, #10b981)" color="white" align="center" justify="center" fontSize="xs">
												<FontAwesomeIcon icon={faListAlt} />
											</Flex>
											<Text fontWeight="600" fontSize="xs">Broadcast Channel</Text>
										</HStack>
										<Button size="xs" bgGradient="linear(to-r, #0ea5e9, #10b981)" color="white" _hover={{ bgGradient: "linear(to-r, #0284c7, #059669)" }}>Share</Button>
									</Flex>

									<Divider borderColor={cardBorder} my={1} />

									{shareMembers
										.filter(m => m.email.toLowerCase().trim() !== user?.signInDetails?.loginId?.toLowerCase()?.trim())
										.map(m => (
											<Flex
												key={m.email}
												p={2.5}
												align="center"
												justify="space-between"
												borderRadius="xl"
												border="1px solid"
												borderColor={cardBorder}
												cursor="pointer"
												onClick={() => executeShare(m.email)}
												_hover={{ bg: cardSubtleBg }}
												transition="all 0.15s ease"
											>
												<HStack spacing={2.5}>
													<Avatar size="xs" name={m.email} src={m.avatarUrl} />
													<Text fontSize="xs" fontWeight="500">{m.email}</Text>
												</HStack>
												<Button size="xs" variant="outline" borderColor={cardBorder}>Share</Button>
											</Flex>
										))}
								</VStack>
							)}
						</VStack>
					</ModalBody>
					<ModalFooter borderTop="1px solid" borderColor={cardBorder} py={3}>
						<Button size="sm" variant="ghost" onClick={() => setShareModalOpen(false)} borderRadius="lg">Cancel</Button>
					</ModalFooter>
				</ModalContent>
			</Modal>
		</Center>
	)
}
